import styled from 'styled-components'

const INK = 'var(--vo-ink)'
const MUTED = 'var(--vo-slate)'
const BLUE = 'var(--color-brand)'
const BORDER = 'var(--vo-line)'
const LIGHT_BG = 'var(--vo-cloud)'

export const PageWrapper = styled.div`
  background: var(--vo-mist);
  min-height: 100vh;
`

export const ExternalHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: white;
  border-bottom: 1px solid var(--vo-cloud);
  box-shadow: 0 1px 4px color-mix(in srgb, var(--vo-escuro) 6%, transparent);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
`

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const HeaderLogo = styled.img`
  height: 32px;
  width: auto;
  object-fit: contain;
`

// Text wordmark shown when the tenant has no usable logo image (none configured, or every
// candidate URL failed to load).
export const HeaderLogoText = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 32px;
  color: var(--color-brand);
  white-space: nowrap;
`

export const HeaderDivider = styled.div`
  width: 1px;
  height: 28px;
  background: var(--vo-cloud);
  flex-shrink: 0;
`

export const HeaderCompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const HeaderRequestedLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
`

export const HeaderCompanyName = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--vo-ink);
`

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const HeaderDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--vo-success);
  flex-shrink: 0;
`

export const HeaderLinkStatus = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-success);
`

export const PageContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 104px 24px 80px;
`

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`

export const ErrorContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 64px 24px;
  text-align: center;
`

// "Already submitted" is a confirmation, not an error: neutral ink title, success check.
export const SubmittedCard = styled.div`
  background: white;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  padding: 40px 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  svg {
    width: 48px;
    height: 48px;
    color: var(--vo-success);
  }
`

export const SubmittedTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`

export const RemediationNoteAlert = styled.div`
  background: color-mix(in srgb, var(--vo-warning) 10%, white);
  border: 1px solid color-mix(in srgb, var(--vo-warning) 35%, transparent);
  border-radius: 12px;
  padding: 14px 18px;
`

export const RemediationNoteTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 6px;
`

export const RemediationNoteText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  margin: 0;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

export const ErrorTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: var(--vo-danger);
  margin: 0 0 8px;
`

export const ErrorText = styled.p`
  font-family: 'Lato', sans-serif;
  color: ${MUTED};
  margin: 0;
`

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

export const HeaderTitle = styled.h1`
  font-family: 'Rubik', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 6px;
  letter-spacing: -0.02em;
`

export const HeaderMeta = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${MUTED};
  margin: 0 0 20px;
  strong { color: var(--vo-ink-2); }
`

export const ProgressSection = styled.div``

export const ProgressLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`

export const ProgressLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
`

export const ProgressValue = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${BLUE};
`

export const ProgressTrack = styled.div`
  height: 8px;
  background: ${LIGHT_BG};
  border-radius: 4px;
  overflow: hidden;
`

export const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, var(--color-brand-strong), var(--vo-capri));
  border-radius: 4px;
  transition: width 0.3s ease;
`

export const InstructionAlert = styled.div`
  background: var(--vo-capri-50);
  border: 1px solid color-mix(in srgb, var(--vo-info) 20%, transparent);
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

export const InstructionText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${BLUE};
  margin: 0;
  line-height: 1.6;
`

export const AccordionCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`

export const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  &:hover { background: var(--vo-capri-25); }
`

export const AccordionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const DomainName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
`

export const DomainProgressBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  background: var(--vo-mist);
  padding: 2px 10px;
  border-radius: 20px;
`

export const ChevronWrapper = styled.div`
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
  display: flex;
  color: ${MUTED};
  flex-shrink: 0;
`

export const AccordionBody = styled.div`
  border-top: 1px solid ${LIGHT_BG};
  padding: 20px;
`

export const ControlWrapper = styled.div`
  margin-bottom: ${({ $last }) => $last ? '0' : '24px'};
  padding-bottom: ${({ $last }) => $last ? '0' : '24px'};
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid var(--vo-mist)'};
`

export const ControlHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
`

export const ControlTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
  flex: 1;
`

export const LockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  border: 1px solid ${BORDER};
  white-space: nowrap;
  flex-shrink: 0;
`

export const MaturityRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
`

export const RadioLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border: 1.5px solid ${({ $selected }) => $selected ? BLUE : BORDER};
  background: ${({ $selected }) => $selected ? 'var(--vo-capri-25)' : 'white'};
  border-radius: 10px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  transition: border-color 0.15s, background 0.15s;
  &:hover:not([style*="not-allowed"]) { border-color: ${BLUE}; background: var(--vo-capri-25); }
`

export const RadioInput = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${BLUE};
  flex-shrink: 0;
  margin-top: 1px;
  cursor: pointer;
  &:disabled { cursor: not-allowed; }
`

export const LevelLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`

export const LevelDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`

export const EvidenceSection = styled.div`
  margin-top: 14px;
`

export const EvidenceLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0 0 8px;
`

export const StyledUploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  background: white;
  color: ${BLUE};
  font-family: 'Maven Pro', sans-serif;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--vo-capri-50); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

export const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 4px 12px;
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  background: white;
  color: ${MUTED};
  font-size: 12px;
  cursor: pointer;
  &:hover { background: var(--vo-mist); }
`

export const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
`

export const FileItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`

export const FileIconWrapper = styled.span`
  display: flex;
  align-items: center;
  min-width: 24px;
  color: ${({ $color }) => $color || 'var(--vo-slate)'};
`

export const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`

export const FileName = styled.span`
  display: block;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FileMeta = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ $malicious }) => $malicious ? 'var(--vo-danger)' : 'var(--vo-slate)'};
`

export const ScanChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $variant }) =>
    $variant === 'success' ? 'var(--vo-success-10)' :
    $variant === 'error' ? 'var(--vo-danger-10)' :
    $variant === 'warning' ? 'var(--vo-warning-10)' :
    'var(--vo-mist)'};
  color: ${({ $variant }) =>
    $variant === 'success' ? 'var(--vo-success)' :
    $variant === 'error' ? 'var(--vo-danger)' :
    $variant === 'warning' ? 'var(--vo-warning)' :
    'var(--vo-slate)'};
`

export const ActionRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
`

export const SavingIndicator = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${MUTED};
`

export const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 52px;
  padding: 0 32px;
  border: none;
  border-radius: 12px;
  background: ${BLUE};
  color: white;
  font-family: 'Maven Pro', sans-serif;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--color-brand-strong); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
